import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Rotating announcement carousel for the NA announcement bar.
 *
 * Reproduces the Ring Concierge announcement animation with vanilla JS: a
 * full-width horizontal slide (translateX) with ease-out, autoplay, infinite
 * wrap-around and finger/pointer drag — no arrows.
 *
 * Mechanics: the track always rests at translateX(-100%) with the current
 * slide at child index 1 (so a slide sits off-screen on each side as a drag
 * buffer). Advancing animates to -200% then moves the first child to the end
 * and snaps back to -100%; going back animates to 0% then moves the last child
 * to the front. With only two announcements the set is cloned once so the
 * buffers always exist.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
export class NaAnnouncementCarousel extends Component {
    /** @type {AbortController} */
    #abortController = new AbortController();

    /** @type {AbortController} */
    #dragAbortController = new AbortController();

    /** @type {number|undefined} */
    #interval = undefined;

    #realCount = 0;
    #transitioning = false;
    #dragging = false;
    #userPaused = false;
    #dragStartX = 0;
    #dragDelta = 0;
    #dragStartTime = 0;
    #suppressClick = false;

    /** @type {number|undefined} */
    #resizeRaf = undefined;

    connectedCallback() {
        super.connectedCallback();
        this.#setup();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#stopAutoplay();
        this.#abortController.abort();
        this.#dragAbortController.abort();
    }

    updatedCallback() {
        // Section Rendering API re-rendered the section — rebuild from scratch.
        this.#stopAutoplay();
        this.#abortController.abort();
        this.#dragAbortController.abort();
        this.#abortController = new AbortController();
        this.#dragAbortController = new AbortController();
        this.#dragging = false;
        this.#transitioning = false;
        this.#userPaused = false;
        this.#setup();
    }

    get #track() {
        return this.refs.track ?? null;
    }

    /** @returns {HTMLElement[]} */
    get #children() {
        const track = this.#track;
        return track ? /** @type {HTMLElement[]} */ ([...track.children]) : [];
    }

    get #reduceMotion() {
        return prefersReducedMotion();
    }

    get #autoplayMs() {
        const value = parseFloat(this.getAttribute('autoplay') ?? '');
        return Number.isFinite(value) && value > 0 ? value * 1000 : 0;
    }

    #setup() {
        const track = this.#track;
        if (!track) return;

        track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());

        this.#realCount = track.children.length;

        this.#userPaused = false;
        this.#syncPauseButton();

        if (this.#realCount < 2) {
            this.#instant(() => {
                track.style.transform = 'translateX(0)';
            }, true);
            if (this.refs.pauseButton) this.refs.pauseButton.hidden = true;
            return;
        }

        if (this.refs.pauseButton) this.refs.pauseButton.hidden = false;

        if (this.#realCount === 2) {
            for (const slide of [...track.children]) {
                const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
                clone.setAttribute('data-clone', '');
                track.append(clone);
            }
        }

        // Move the last slide in front so the first real slide rests at index 1.
        // Deferred: a forced reflow here lands mid-load, when the whole page layout is still dirty.
        this.#instant(() => {
            if (track.lastElementChild) track.prepend(track.lastElementChild);
            track.style.transform = 'translateX(-100%)';
        }, true);

        this.#syncA11y();
        this.#bindEvents();
        this.#measure();
        if (document.fonts) document.fonts.ready.then(this.#measure);
        this.#startAutoplay();
    }

    #bindEvents() {
        const { signal } = this.#abortController;
        const track = this.#track;
        if (!track) return;

        this.addEventListener('mouseenter', this.#pauseAutoplay, { signal });
        this.addEventListener('mouseleave', this.#resumeAutoplay, { signal });
        this.addEventListener('focusin', this.#pauseAutoplay, { signal });
        this.addEventListener('focusout', this.#onFocusOut, { signal });
        this.addEventListener('keydown', this.#onKeyDown, { signal });
        this.addEventListener('click', this.#onClickCapture, { signal, capture: true });
        document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });

        track.addEventListener('pointerdown', this.#onPointerDown, { signal });
        window.addEventListener('resize', this.#onResize, { signal });
    }

    /**
     * Applies a transform change with no transition (no visible animation).
     * @param {() => void} apply
     * @param {boolean} [deferred] Release the lock on a later frame instead of forcing a reflow.
     */
    #instant(apply, deferred = false) {
        const track = this.#track;
        if (!track) return;
        track.setAttribute('data-instant', '');
        apply();
        if (deferred) {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    if (!this.#dragging) track.removeAttribute('data-instant');
                })
            );
            return;
        }
        void track.offsetWidth; // force reflow so the change commits without a transition
        track.removeAttribute('data-instant');
    }

    /**
     * Animates the track to a transform value, resolving when it settles.
     * @param {string} value
     * @returns {Promise<void>}
     */
    #animateTo(value) {
        const track = this.#track;
        if (!track) return Promise.resolve();

        if (this.#reduceMotion) {
            this.#instant(() => {
                track.style.transform = value;
            });
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                track.removeEventListener('transitionend', onEnd);
                resolve();
            };
            /** @param {TransitionEvent} event */
            const onEnd = (event) => {
                if (event.target === track && event.propertyName === 'transform') finish();
            };
            track.addEventListener('transitionend', onEnd);
            setTimeout(finish, this.#durationMs() + 100);
            requestAnimationFrame(() => {
                track.style.transform = value;
            });
        });
    }

    #durationMs() {
        const track = this.#track;
        if (!track) return 800;
        const raw = getComputedStyle(track).transitionDuration.split(',')[0]?.trim() ?? '0s';
        const ms = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
        return Number.isFinite(ms) && ms > 0 ? ms : 800;
    }

    async next() {
        const track = this.#track;
        if (!track || this.#transitioning || this.#realCount < 2) return;
        this.#transitioning = true;
        await this.#animateTo('translateX(-200%)');
        this.#instant(() => {
            if (track.firstElementChild) track.append(track.firstElementChild);
            track.style.transform = 'translateX(-100%)';
        }, true);
        this.#syncA11y();
        this.#transitioning = false;
    }

    async previous() {
        const track = this.#track;
        if (!track || this.#transitioning || this.#realCount < 2) return;
        this.#transitioning = true;
        await this.#animateTo('translateX(0)');
        this.#instant(() => {
            if (track.lastElementChild) track.prepend(track.lastElementChild);
            track.style.transform = 'translateX(-100%)';
        }, true);
        this.#syncA11y();
        this.#transitioning = false;
    }

    #syncA11y() {
        this.#children.forEach((child, index) => {
            const hidden = index !== 1;
            child.setAttribute('aria-hidden', String(hidden));
            child.toggleAttribute('inert', hidden);
        });
    }

    /**
     * On desktop, size the slider to the widest announcement so the layout
     * stays left-aligned and the countdown never jumps as messages rotate.
     * A CSS min-width provides the floor. Mobile keeps the full-width slider.
     */
    #measure = () => {
        const track = this.#track;
        if (!track || this.#realCount < 2 || this.#dragging || this.#transitioning) return;

        if (!window.matchMedia('(min-width: 990px)').matches) {
            this.style.removeProperty('--na-announcement-slider-width');
            return;
        }

        const kids = this.#children;

        for (const kid of kids) {
            const content = /** @type {HTMLElement | null} */ (kid.firstElementChild);
            if (!content) continue;
            content.style.display = 'inline-flex';
            content.style.width = 'auto';
            content.style.whiteSpace = 'nowrap';
        }

        let widest = 0;
        for (const kid of kids) {
            const content = kid.firstElementChild;
            if (content) widest = Math.max(widest, content.getBoundingClientRect().width);
        }

        for (const kid of kids) {
            const content = /** @type {HTMLElement | null} */ (kid.firstElementChild);
            if (!content) continue;
            content.style.display = '';
            content.style.width = '';
            content.style.whiteSpace = '';
        }

        if (widest > 0) this.style.setProperty('--na-announcement-slider-width', `${Math.ceil(widest)}px`);
    };

    #onResize = () => {
        if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
        this.#resizeRaf = requestAnimationFrame(this.#measure);
    };

    #startAutoplay() {
        this.#stopAutoplay();
        if (this.#userPaused || this.#reduceMotion || this.#realCount < 2) return;
        const ms = this.#autoplayMs;
        if (!ms) return;

        this.#interval = setInterval(() => {
            if (
                this.#dragging ||
                this.#transitioning ||
                this.#userPaused ||
                document.hidden ||
                this.matches(':hover') ||
                this.matches(':focus-within')
            ) {
                return;
            }
            this.next();
        }, ms);
    }

    #stopAutoplay() {
        if (this.#interval === undefined) return;
        clearInterval(this.#interval);
        this.#interval = undefined;
    }

    #pauseAutoplay = () => this.#stopAutoplay();

    #resumeAutoplay = () => {
        if (this.#userPaused || this.matches(':hover') || this.matches(':focus-within')) return;
        this.#startAutoplay();
    };

    /** @param {FocusEvent} event */
    #onFocusOut = (event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && this.contains(next)) return;
        this.#resumeAutoplay();
    };

    /** @param {KeyboardEvent} event */
    #onKeyDown = (event) => {
        if (this.#realCount < 2 || this.#transitioning) return;
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

        event.preventDefault();
        const move = event.key === 'ArrowRight' ? this.next() : this.previous();
        move.then(() => {
            if (this.contains(document.activeElement)) return;
            const current = this.#children[1];
            const target = current?.querySelector('a, button');
            if (target instanceof HTMLElement) target.focus();
        });
    };

    #onVisibilityChange = () => (document.hidden ? this.#stopAutoplay() : this.#resumeAutoplay());

    toggleAutoplay() {
        this.#userPaused = !this.#userPaused;
        this.#syncPauseButton();
        if (this.#userPaused) {
            this.#stopAutoplay();
            return;
        }
        this.#startAutoplay();
    }

    #syncPauseButton() {
        const button = this.refs.pauseButton;
        if (!button) return;

        const pauseLabel = button.dataset.pauseLabel ?? '';
        const playLabel = button.dataset.playLabel ?? '';
        button.setAttribute('aria-label', this.#userPaused ? playLabel : pauseLabel);

        const pauseIcon = button.querySelector('[data-pause-icon]');
        const playIcon = button.querySelector('[data-play-icon]');
        if (pauseIcon instanceof HTMLElement) pauseIcon.hidden = this.#userPaused;
        if (playIcon instanceof HTMLElement) playIcon.hidden = !this.#userPaused;
    }

    /** @param {PointerEvent} event */
    #onPointerDown = (event) => {
        if (this.#realCount < 2 || this.#transitioning) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('[ref="pauseButton"]')) return;

        const track = this.#track;
        if (!track) return;

        this.#dragAbortController.abort();
        this.#dragAbortController = new AbortController();
        const { signal } = this.#dragAbortController;
        document.addEventListener('pointermove', this.#onPointerMove, { signal });
        document.addEventListener('pointerup', this.#onPointerUp, { signal });
        document.addEventListener('pointercancel', this.#onPointerUp, { signal });

        this.#dragging = true;
        this.#dragStartX = event.clientX;
        this.#dragDelta = 0;
        this.#dragStartTime = performance.now();
        this.#stopAutoplay();
        this.setAttribute('dragging', '');
        track.setAttribute('data-instant', '');
    };

    /** @param {PointerEvent} event */
    #onPointerMove = (event) => {
        if (!this.#dragging) return;
        const track = this.#track;
        if (!track) return;
        this.#dragDelta = event.clientX - this.#dragStartX;
        track.style.transform = `translateX(calc(-100% + ${this.#dragDelta}px))`;
    };

    /** @param {PointerEvent} event */
    #onPointerUp = async (event) => {
        this.#dragAbortController.abort();
        if (!this.#dragging) return;
        const track = this.#track;
        if (!track) return;

        this.#dragging = false;
        this.removeAttribute('dragging');
        track.removeAttribute('data-instant');

        const delta = this.#dragDelta;
        const width = this.clientWidth || 1;
        const elapsed = performance.now() - this.#dragStartTime;
        const velocity = delta / Math.max(elapsed, 1);
        const threshold = width * 0.2;

        this.#suppressClick = Math.abs(delta) > 5;

        if (delta <= -threshold || velocity < -0.4) {
            await this.next();
        } else if (delta >= threshold || velocity > 0.4) {
            await this.previous();
        } else {
            await this.#animateTo('translateX(-100%)');
        }

        this.#dragDelta = 0;
        this.#resumeAutoplay();
    };

    /** @param {MouseEvent} event */
    #onClickCapture = (event) => {
        if (!this.#suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.#suppressClick = false;
    };
}

if (!customElements.get('na-announcement-carousel-component')) {
    customElements.define('na-announcement-carousel-component', NaAnnouncementCarousel);
}
